import React, { useCallback, useEffect, useState } from 'react';
import { Shield, ShieldCheck, ShieldOff, Eye, EyeOff, Copy, Check } from 'lucide-react';
import api from '../services/api';

const fecha = (d) => (d ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

/**
 * Cifrado de disco (BitLocker / FileVault) desde Intune. Aparece solo cuando Intune responde; mientras falte el
 * consentimiento de la app de Azure no muestra la tarjeta (avisa con una nota discreta) y se mantiene el registro manual.
 * onEstado(estado) permite al padre ocultar la sección manual cuando Intune ya entrega los datos.
 */
export default function IntuneBitLockerPanel({ activoId, onEstado }) {
    const [d, setD] = useState(null);
    const [claves, setClaves] = useState({}); // keyId -> { valor, cargando, error }
    const [copiada, setCopiada] = useState(null);

    const cargar = useCallback(async () => {
        try {
            const r = await api.get(`/intune/activo/${activoId}`);
            setD(r.data);
            onEstado?.(r.data.estado);
        } catch {
            setD({ estado: 'error' });
            onEstado?.('error');
        }
    }, [activoId, onEstado]);

    useEffect(() => { cargar(); }, [cargar]);

    const mostrar = async (keyId) => {
        setClaves((c) => ({ ...c, [keyId]: { cargando: true } }));
        try {
            const r = await api.post(`/intune/activo/${activoId}/clave/${keyId}`);
            setClaves((c) => ({ ...c, [keyId]: { valor: r.data.clave } }));
        } catch (e) {
            setClaves((c) => ({ ...c, [keyId]: { error: e.response?.data?.message || 'No se pudo obtener la clave.' } }));
        }
    };
    const ocultar = (keyId) => setClaves((c) => { const n = { ...c }; delete n[keyId]; return n; });
    const copiar = async (keyId, valor) => {
        try { await navigator.clipboard.writeText(valor); setCopiada(keyId); setTimeout(() => setCopiada(null), 1500); } catch { /* sin portapapeles */ }
    };

    if (!d) return null;
    if (d.estado === 'sin_permisos') {
        return <p className="text-xs text-gray-400 mb-4">Cifrado de disco desde Intune: pendiente de consentimiento en Azure. Aparecerá aquí automáticamente cuando se conceda.</p>;
    }
    if (d.estado !== 'ok') return null;

    const x = d.dispositivo || {};
    const cifrado = d.cifrado;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-orange-600" /> {d.tecnologia} <span className="ml-2 text-xs font-normal text-gray-400">desde Intune</span>
                </h3>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${cifrado === true ? 'bg-emerald-100 text-emerald-700' : cifrado === false ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                    {cifrado === true ? <ShieldCheck size={16} /> : <ShieldOff size={16} />}
                    {cifrado === true ? 'Disco cifrado' : cifrado === false ? 'Disco sin cifrar' : 'Sin dato de cifrado'}
                </span>
            </div>
            <div className="p-4 space-y-4">
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
                    {[
                        ['Cumplimiento', x.cumplimiento || '—'],
                        ['Última sincronización', fecha(x.ultimaSincronizacion)],
                        ['Sistema', [x.sistema, x.version].filter(Boolean).join(' ') || '—'],
                        ['Número de serie', x.serie || '—'],
                        ['Usuario', x.usuario || '—'],
                        ['Equipo', [x.fabricante, x.modelo].filter(Boolean).join(' ') || '—'],
                        ['Enrolado', fecha(x.enrolado)],
                    ].map(([k, v]) => (
                        <div key={k}><dt className="text-xs text-gray-500 dark:text-gray-400">{k}</dt><dd className="mt-0.5 font-medium text-gray-900 dark:text-white break-words">{v}</dd></div>
                    ))}
                </dl>

                {d.tecnologia === 'BitLocker' && (
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Claves de recuperación</h4>
                        {d.clavesDisponibles === false && <p className="text-sm text-amber-600">Falta el permiso BitLockerKey.Read.All para leer las claves.</p>}
                        {d.clavesDisponibles && d.claves.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{cifrado ? 'Intune no tiene ninguna clave respaldada para este equipo.' : 'Sin claves: el disco no está cifrado con BitLocker.'}</p>
                        )}
                        <ul className="space-y-2">
                            {d.claves.map((k) => {
                                const est = claves[k.id];
                                return (
                                    <li key={k.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="text-sm">
                                                <span className="font-medium text-gray-900 dark:text-white">{k.volumen || 'Volumen'}</span>
                                                <span className="text-xs text-gray-500 ml-2">respaldada {fecha(k.fecha)}</span>
                                                <span className="block text-[11px] font-mono text-gray-400">{k.id}</span>
                                            </div>
                                            {est?.valor ? (
                                                <button type="button" onClick={() => ocultar(k.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"><EyeOff size={14} /> Ocultar</button>
                                            ) : (
                                                <button type="button" disabled={est?.cargando} onClick={() => mostrar(k.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"><Eye size={14} /> {est?.cargando ? 'Consultando…' : 'Mostrar clave'}</button>
                                            )}
                                        </div>
                                        {est?.valor && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <code className="flex-1 px-3 py-2 rounded bg-gray-50 dark:bg-gray-900 text-sm font-mono break-all text-gray-900 dark:text-white">{est.valor}</code>
                                                <button type="button" onClick={() => copiar(k.id, est.valor)} className="p-2 rounded border border-gray-300 dark:border-gray-600" aria-label="Copiar clave">{copiada === k.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}</button>
                                            </div>
                                        )}
                                        {est?.error && <p className="mt-2 text-sm text-red-600">{est.error}</p>}
                                    </li>
                                );
                            })}
                        </ul>
                        {d.claves.length > 0 && <p className="mt-2 text-[11px] text-gray-400">Cada vez que se muestra una clave queda registrado en la auditoría (quién y cuándo).</p>}
                    </div>
                )}
                {d.tecnologia === 'FileVault' && <p className="text-sm text-gray-500">Equipo Mac: el cifrado es con FileVault; la clave de recuperación se gestiona desde Intune.</p>}
            </div>
        </div>
    );
}
